import { useParams } from "react-router-dom";
import { useMemo, useState } from "react";
import { ChevronRight, ChevronDown, Plus, Search, User } from "lucide-react";
import { ClientEntityType } from "../../../../../../../lib/supabase/supabaseTypes";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { AppBarClientsListProps } from "../../app-bar-types";
import { ClientSidebarItem } from "../ClientSidebarItem";
import { filterActiveClients, sortClients } from "@/lib/utils/client-utils";
import {
	Dialog,
	DialogContent,
	DialogTitle,
	VisuallyHidden,
} from "@/dentlab/src/components/ui/dialog";
import { ClientForm } from "@/dentlab/src/forms/form-components/client/insert/client.form";
import {
	SheetContentType,
	useSheetStore,
} from "@/dentlab/src/store/Sheets/sheet.store";
import { getLocalStorage, setLocalStorage } from "@/lib/utils/local-storage";
import { useRealtimeStore } from "@/lib/store/Realtime";
import { UnreadIndicator } from "@/dentlab/src/components/chat/unread-indicator.component";

const STORAGE_KEY = "dentlab-sidebar-clients-collapsed";

export const AppBarClientsList: React.FC<AppBarClientsListProps> = ({
	expanded,
	showCode = false,
	maxItems,
	onClientInteraction,
	onCollapseChange,
	onOverflowClick,
}) => {
	const { clientId } = useParams();
	const [dialogOpen, setDialogOpen] = useState(false);
	const [isCollapsed, setIsCollapsed] = useState(() =>
		getLocalStorage(STORAGE_KEY, false)
	);
	const visibleCount = maxItems ?? Infinity;

	const {
		clientsLookup,
		connectedOrgIdToClientId,
		sortClientsByCode,
		myOrgId,
	} = useCentralStore((state) => {
		const config = state.organization?.config as any;
		return {
			clientsLookup: state.clientsLookup,
			connectedOrgIdToClientId: state.connectedOrgIdToClientId,
			sortClientsByCode: config?.sort_clients_by_code ?? false,
			myOrgId: state.organization?.id ?? "",
		};
	});

	const { openSheet, sheets } = useSheetStore((state) => ({
		openSheet: state.openSheet,
		sheets: state.sheets,
	}));

	const unreadCounts = useRealtimeStore((state) =>
		state.getUnreadCountsByClientId(connectedOrgIdToClientId, myOrgId)
	);

	const filteredAndSortedClients = useMemo(() => {
		const activeClients = filterActiveClients(Object.values(clientsLookup));
		return sortClients(activeClients, sortClientsByCode);
	}, [clientsLookup, sortClientsByCode]);

	const hasClients = filteredAndSortedClients.length > 0;
	const showExpandedEmptyState = expanded && !hasClients;

	const totalClients = filteredAndSortedClients.length;
	const needsOverflow = !isCollapsed && totalClients > visibleCount;
	const displayCount = needsOverflow ? visibleCount - 1 : totalClients;
	const visibleClients = isCollapsed
		? []
		: filteredAndSortedClients.slice(0, displayCount);
	const hiddenCount = totalClients - displayCount;

	const hiddenUnreadSum = useMemo(() => {
		if (!needsOverflow) return 0;
		const visibleIds = new Set(visibleClients.map((c) => c.id));
		let sum = 0;
		for (const [id, count] of Object.entries(unreadCounts)) {
			if (!visibleIds.has(id)) sum += count;
		}
		return sum;
	}, [needsOverflow, visibleClients, unreadCounts]);

	const handleToggleCollapse = () => {
		const newValue = !isCollapsed;
		setIsCollapsed(newValue);
		setLocalStorage(STORAGE_KEY, newValue);
		onCollapseChange?.(newValue);
	};

	const handleAddClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		setDialogOpen(true);
	};

	const handleSearchClick = (e: React.MouseEvent) => {
		e.stopPropagation();
		if (
			sheets.length === 0 ||
			sheets[sheets.length - 1].sheetContentType !==
				SheetContentType.SEARCH
		) {
			openSheet(SheetContentType.SEARCH);
		}
	};

	const handleOverflowClick = () => {
		onOverflowClick?.();
	};

	return (
		<div
			className="flex flex-col w-full overflow-hidden"
			data-testid="app-bar-client-list"
		>
			{expanded ? (
				<button
					onClick={handleToggleCollapse}
					className="flex-shrink-0 flex items-center w-full px-2 py-1 text-sm font-medium text-gray-700 hover:bg-stone-200 rounded-md transition-colors"
					aria-expanded={!isCollapsed}
					aria-label="Kunden ein-/ausklappen"
					data-testid="clients-list-toggle"
				>
					{isCollapsed ? (
						<ChevronRight size={20} className="flex-shrink-0" />
					) : (
						<ChevronDown size={20} className="flex-shrink-0" />
					)}
					<span className="ml-1 flex-1 text-left">Kunden</span>
					{hasClients && (
						<div
							onClick={handleSearchClick}
							className="flex-shrink-0 p-0.5 rounded hover:bg-stone-300 transition-colors"
							aria-label="Suche öffnen"
							data-testid="client-sidebar-search-button"
						>
							<Search size={18} />
						</div>
					)}
					<div
						onClick={handleAddClick}
						className="flex-shrink-0 p-0.5 rounded hover:bg-stone-300 transition-colors ml-1"
						aria-label="Neuen Kunden hinzufügen"
						data-testid="client-sidebar-add-button"
					>
						<Plus size={18} />
					</div>
				</button>
			) : (
				<div
					onClick={handleToggleCollapse}
					className="relative flex justify-center py-1 rounded-md cursor-pointer transition-colors duration-200 text-gray-700 hover:bg-stone-200"
					data-testid="clients-list-toggle"
					aria-label="Kunden ein-/ausklappen"
				>
					<User size={18} />
					{hiddenUnreadSum > 0 && (
						<UnreadIndicator
							count={hiddenUnreadSum}
							showCount={false}
							className="absolute top-0.5 right-0.5"
						/>
					)}
				</div>
			)}

			{!isCollapsed && hasClients && (
				<div className="flex flex-col overflow-hidden gap-0.5">
					{visibleClients.map(
						(client: ClientEntityType & { image_src?: string }) => (
							<ClientSidebarItem
								key={client.id}
								client={client}
								isSelected={clientId === client.id}
								expanded={expanded}
								showCode={showCode}
								onClientInteraction={onClientInteraction}
								unreadCount={
									client.id ? unreadCounts[client.id] || 0 : 0
								}
							/>
						)
					)}
					{needsOverflow && expanded && (
						<button
							onClick={handleOverflowClick}
							className="mt-auto flex items-center px-2 py-1 text-sm text-gray-500 hover:text-gray-700 transition-colors rounded-md hover:bg-stone-200 flex-shrink-0"
							data-testid="clients-overflow-button"
							aria-label={`${hiddenCount} weitere Kunden anzeigen`}
						>
							<span className="flex-1 text-left">
								+{hiddenCount} weitere
							</span>
							<UnreadIndicator
								count={hiddenUnreadSum}
								showCount={true}
								className="flex-shrink-0"
							/>
						</button>
					)}
				</div>
			)}

			{showExpandedEmptyState && (
				<div className="flex-1 min-h-0 mt-2 px-2 pb-2 flex">
					<button
						onClick={handleAddClick}
						className="group w-full h-1/2 min-h-[180px] rounded-xl border border-dashed border-stone-300 bg-white/70 hover:bg-white shadow-sm transition-colors flex flex-col items-center justify-center text-center px-4"
						aria-label="Ersten Kunden hinzufügen"
						data-testid="client-empty-state-add-button"
						type="button"
					>
						<div className="w-10 h-10 rounded-full bg-stone-200 flex items-center justify-center group-hover:bg-stone-300 transition-colors">
							<Plus size={20} className="text-gray-700" />
						</div>
						<span className="mt-2 text-sm font-semibold text-gray-800">
							Ersten Kunden anlegen
						</span>
						<span className="text-xs text-gray-500 mt-1">
							Füge deinen ersten Kunden hinzu, um Aufträge zu
							starten.
						</span>
					</button>
				</div>
			)}

			<Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
				<DialogContent className="flex flex-col">
					<VisuallyHidden>
						<DialogTitle>Neuer Kunde</DialogTitle>
					</VisuallyHidden>
					<ClientForm onSave={() => setDialogOpen(false)} />
				</DialogContent>
			</Dialog>
		</div>
	);
};
